// Edit-mode UI for a Report. Owns a local draft (cloned panels). Save
// commits to localStorage via reports-store.saveReport; Discard throws
// away the draft and navigates back. Drag-and-drop follows the same
// HTML5 pattern as the Query Console tab reorder.

import { useState } from 'react';
import { I } from '../icons.jsx';
import { Panel } from './Panel.jsx';
import { saveReport, newPanelId } from '../../data/reports-store.js';

const VIZ_OPTIONS   = ['kpi', 'table', 'bar', 'pie', 'line'];
const WIDTH_OPTIONS = ['full', 'half', 'third'];

export function ReportEdit({ report, onSaved, onDiscard }) {
  const [draft, setDraft] = useState(() => ({
    ...report,
    panels: (report.panels || []).map(p => ({ ...p })),
  }));
  const [dragId, setDragId] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);
  const [previewIds, setPreviewIds] = useState({});

  const updateMeta  = (patch) => setDraft(d => ({ ...d, ...patch }));
  const updatePanel = (id, patch) => setDraft(d => ({
    ...d,
    panels: d.panels.map(p => p.id === id ? { ...p, ...patch } : p),
  }));
  const addPanel = () => setDraft(d => ({
    ...d,
    panels: [...d.panels, {
      id: newPanelId(),
      title: 'New panel',
      sql: '',
      viz: 'table',
      width: 'full',
      autoUpdate: false,
    }],
  }));
  const removePanel = (id) => setDraft(d => ({
    ...d,
    panels: d.panels.filter(p => p.id !== id),
  }));
  const reorderPanel = (fromId, insertAt) => setDraft(d => {
    const from = d.panels.findIndex(p => p.id === fromId);
    if (from < 0) return d;
    const to = insertAt > from ? insertAt - 1 : insertAt;
    if (to === from) return d;
    const next = d.panels.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return { ...d, panels: next };
  });

  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(id)); } catch { /* ignore */ }
  };
  const onDragOverPanel = (e, idx) => {
    if (dragId == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const r = e.currentTarget.getBoundingClientRect();
    const before = (e.clientY - r.top) < r.height / 2;
    setDropIdx(before ? idx : idx + 1);
  };
  const onDrop = (e) => {
    e.preventDefault();
    if (dragId != null && dropIdx != null) reorderPanel(dragId, dropIdx);
    setDragId(null);
    setDropIdx(null);
  };
  const onDragEnd = () => { setDragId(null); setDropIdx(null); };

  const save = () => {
    try {
      const saved = saveReport(draft);
      onSaved?.(saved);
    } catch (e) {
      // Editing a built-in needs a duplicate first — surface that as a
      // simple alert; the higher-level "Edit" button handles cloning.
      window.alert('Save failed: ' + (e.message || e));
    }
  };

  const togglePreview = (id) => setPreviewIds(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="report-edit">
      <div className="report-edit-bar">
        <button className="btn btn-primary btn-sm" onClick={save} title="Save (writes to localStorage)">
          <I.Save size={12} /> Save
        </button>
        <button className="btn btn-ghost btn-sm" onClick={onDiscard} title="Throw away changes">
          Discard
        </button>
        <span className="report-edit-hint">Edits stay in this browser until backend storage lands.</span>
      </div>
      <div className="report-edit-meta">
        <label>
          <span>Name</span>
          <input
            type="text"
            value={draft.name || ''}
            onChange={e => updateMeta({ name: e.target.value })}
            placeholder="Report name"
          />
        </label>
        <label>
          <span>Description</span>
          <input
            type="text"
            value={draft.description || ''}
            onChange={e => updateMeta({ description: e.target.value })}
            placeholder="Optional"
          />
        </label>
      </div>
      <div className="report-edit-panels" onDrop={onDrop} onDragOver={e => e.preventDefault()}>
        {draft.panels.length === 0 && (
          <div className="report-empty">No panels yet — click <strong>+ Add panel</strong> below.</div>
        )}
        {draft.panels.map((p, idx) => (
          <PanelEditor
            key={p.id}
            panel={p}
            isDragging={dragId === p.id}
            showDropAbove={dragId != null && dropIdx === idx     && dragId !== p.id}
            showDropBelow={dragId != null && dropIdx === idx + 1 && dragId !== p.id}
            preview={!!previewIds[p.id]}
            onChange={patch => updatePanel(p.id, patch)}
            onRemove={() => removePanel(p.id)}
            onTogglePreview={() => togglePreview(p.id)}
            onDragStart={e => onDragStart(e, p.id)}
            onDragOver={e => onDragOverPanel(e, idx)}
            onDragEnd={onDragEnd}
          />
        ))}
        <button className="btn btn-ghost report-edit-add" onClick={addPanel}>
          <I.Plus size={12} /> Add panel
        </button>
      </div>
    </div>
  );
}

function PanelEditor({
  panel, preview, isDragging, showDropAbove, showDropBelow,
  onChange, onRemove, onTogglePreview,
  onDragStart, onDragOver, onDragEnd,
}) {
  return (
    <div
      className={
        'panel-editor'
        + (isDragging     ? ' dragging' : '')
        + (showDropAbove  ? ' drop-above' : '')
        + (showDropBelow  ? ' drop-below' : '')
      }
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <div className="panel-editor-head">
        <span className="panel-editor-grip" title="Drag to reorder"><I.More size={14} /></span>
        <input
          type="text"
          className="panel-editor-title"
          value={panel.title || ''}
          onChange={e => onChange({ title: e.target.value })}
          placeholder="Panel title"
        />
        <button
          type="button"
          className={'btn btn-sm ' + (preview ? 'btn-primary' : 'btn-ghost')}
          onClick={onTogglePreview}
          title="Toggle live preview of this panel"
        >
          <I.Eye size={12} /> Preview
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={onRemove}
          title="Remove panel"
        >
          <I.Close size={12} />
        </button>
      </div>
      <div className="panel-editor-body">
        <label className="panel-editor-row">
          <span>SQL</span>
          <textarea
            className="panel-editor-sql"
            value={panel.sql || ''}
            onChange={e => onChange({ sql: e.target.value })}
            rows={4}
            placeholder="SHOW PEERS  ·  SELECT … FROM <table>  ·  any single statement"
            spellCheck={false}
          />
          <span className="panel-editor-sql-hint">
            Time-bound with <code>{'time > {{since:15m}} AND time <= {{until}}'}</code> — such
            panels follow the report timeframe picker and chart drag-zoom. For line charts,
            downsample with <code>{'time_bucket(time, {{bucket}})'}</code> — <code>{'{{bucket}}'}</code> resolves
            to a µs width sized to the active window (~400 points).
          </span>
        </label>
        <div className="panel-editor-row inline">
          <label>
            <span>Viz</span>
            <select value={panel.viz || 'table'} onChange={e => onChange({ viz: e.target.value })}>
              {VIZ_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
          <label>
            <span>Width</span>
            <select value={panel.width || 'full'} onChange={e => onChange({ width: e.target.value })}>
              {WIDTH_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
          <label className="panel-editor-checkbox">
            <input
              type="checkbox"
              checked={!!panel.autoUpdate}
              onChange={e => onChange({ autoUpdate: e.target.checked })}
            />
            <span>Auto-update by default</span>
          </label>
        </div>
        {preview && (
          <div className="panel-editor-preview">
            <Panel panel={panel} autoEffective={false} />
          </div>
        )}
      </div>
    </div>
  );
}
